import React, { useState, useEffect } from 'react';
import { Toolbar, ToolbarSearch, ToolbarSelect, ToolbarSpacer, StatusBadge, Banner, Pagination } from '@/components/AdminPanel/AdminPrimitives';
import { LCBtn, LCIcon, CLS } from '@/components/common/LCPrimitives';
import apiService from '@/services/apiService';
import { ICOverride } from '@/types/admin';
import { useAuth } from '@/contexts/AuthContext';
import ICOverrideDialog from '@/components/AdminPanel/ICOverrides/ICOverrideDialog';
import ConfirmationDialog from '@/components/AdminPanel/UserManagement/ConfirmationDialog';

type ConfirmState = {
  open: boolean; title: string; message: string;
  onConfirm: () => void; severity?: 'warning' | 'error' | 'info';
};

const ICOverrides: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [overrides, setOverrides] = useState<ICOverride[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editingOverride, setEditingOverride] = useState<ICOverride | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>({
    open: false, title: '', message: '', onConfirm: () => {}, severity: 'warning',
  });

  const isSuperUser = currentUser?.role === 'super';

  const fetchICOverrides = async () => {
    setLoading(true); setError(null);
    try {
      const params: any = { page: page + 1, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (activeFilter) params.is_active = activeFilter === 'true';
      const data = await apiService.getICOverrides(params);
      setOverrides(data.overrides); setTotal(data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch IC overrides');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchICOverrides(); }, [page, pageSize, searchTerm, activeFilter]);

  const handleCreate = () => { setDialogMode('create'); setEditingOverride(null); setDialogOpen(true); };
  const handleEdit = (o: ICOverride) => { setDialogMode('edit'); setEditingOverride(o); setDialogOpen(true); };

  const handleDeactivate = (o: ICOverride) => setConfirm({
    open: true, title: 'Deactivate IC Override', severity: 'warning',
    message: `Deactivate IC override for portfolio "${o.portfolio_id_lig}"? The portfolio will revert to the official IC.`,
    onConfirm: async () => {
      try {
        await apiService.deactivateICOverride(o.id);
        setSuccess(`IC override for "${o.portfolio_id_lig}" deactivated`);
        fetchICOverrides();
      } catch (err: any) { setError(err.message || 'Failed to deactivate'); }
      setConfirm(p => ({ ...p, open: false }));
    },
  });

  const handleActivate = (o: ICOverride) => setConfirm({
    open: true, title: 'Activate IC Override', severity: 'info',
    message: `Activate IC override for portfolio "${o.portfolio_id_lig}"?`,
    onConfirm: async () => {
      try {
        await apiService.activateICOverride(o.id);
        setSuccess(`IC override for "${o.portfolio_id_lig}" activated`);
        fetchICOverrides();
      } catch (err: any) { setError(err.message || 'Failed to activate'); }
      setConfirm(p => ({ ...p, open: false }));
    },
  });

  return (
    <div className="flex flex-col h-full min-h-0">
      {error && <Banner variant="error" onClose={() => setError(null)} className="mb-3">{error}</Banner>}
      {success && <Banner variant="success" onClose={() => setSuccess(null)} className="mb-3">{success}</Banner>}

      <Toolbar>
        <ToolbarSearch value={searchTerm} onChange={setSearchTerm} placeholder="Search portfolio ID, client, or IC…" />
        <ToolbarSelect value={activeFilter} onChange={setActiveFilter} placeholder="All statuses"
          options={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]} />
        <ToolbarSpacer />
        {!isSuperUser && (
          <LCBtn variant="primary" size="sm" onClick={handleCreate}>
            <LCIcon name="plus" size={12} /> Create Override
          </LCBtn>
        )}
        <button className={CLS.iconBtn} onClick={fetchICOverrides} title="Refresh">
          <LCIcon name="refresh" size={14} />
        </button>
      </Toolbar>

      <div className="flex-1 min-h-0 flex flex-col border border-black/[0.08] bg-white">
        <div className="flex-1 min-h-0 overflow-auto">
          <table className={CLS.table}>
            <thead>
              <tr>
                <th className={CLS.th} style={{ width: 60 }}>ID</th>
                <th className={CLS.th}>Portfolio ID</th>
                <th className={CLS.th}>Client Name</th>
                <th className={CLS.th}>Override IC</th>
                <th className={CLS.th}>Reason</th>
                <th className={CLS.th}>Effective Date</th>
                <th className={CLS.th}>Created By</th>
                <th className={CLS.th}>Status</th>
                <th className={`${CLS.th} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className={`${CLS.td} ${CLS.muted} text-center py-8`}>Loading…</td></tr>
              )}
              {!loading && overrides.length === 0 && (
                <tr><td colSpan={9} className={`${CLS.td} ${CLS.muted} text-center py-8`}>No IC overrides found.</td></tr>
              )}
              {!loading && overrides.map(o => (
                <tr key={o.id} className="hover:bg-[#FAF9F8] transition-colors">
                  <td className={`${CLS.td} ${CLS.tdMono} text-[#7A7A76]`}>{o.id}</td>
                  <td className={`${CLS.td} ${CLS.tdMono}`}>{o.portfolio_id_lig}</td>
                  <td className={CLS.td}>{o.portfolio?.client_name || '—'}</td>
                  <td className={CLS.td}>{o.override_ic_user.full_name}</td>
                  <td className={`${CLS.td} max-w-[220px]`}>
                    <span className="block truncate text-[12px]">{o.reason}</span>
                  </td>
                  <td className={CLS.td}>{new Date(o.effective_date).toLocaleDateString()}</td>
                  <td className={CLS.td}>{o.created_by?.full_name || 'System'}</td>
                  <td className={CLS.td}><StatusBadge active={o.is_active} /></td>
                  <td className={`${CLS.td} text-right`}>
                    {!isSuperUser && (
                      <div className="flex items-center justify-end gap-0.5">
                        <button className={CLS.iconBtn} onClick={() => handleEdit(o)} title="Edit Override">
                          <LCIcon name="edit" size={13} />
                        </button>
                        {o.is_active ? (
                          <button className={`${CLS.iconBtn} hover:text-[#E50025]`} onClick={() => handleDeactivate(o)} title="Deactivate">
                            <LCIcon name="close" size={13} />
                          </button>
                        ) : (
                          <button className={`${CLS.iconBtn} hover:text-[#1f8a4a]`} onClick={() => handleActivate(o)} title="Activate">
                            <LCIcon name="check" size={13} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={total}
          onChange={(p, ps) => { setPage(p); setPageSize(ps); }} />
      </div>

      <ICOverrideDialog
        open={dialogOpen} mode={dialogMode} editingOverride={editingOverride}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => {
          setDialogOpen(false);
          setSuccess(`IC override ${dialogMode === 'create' ? 'created' : 'updated'} successfully`);
          fetchICOverrides();
        }} />

      <ConfirmationDialog
        open={confirm.open} title={confirm.title} message={confirm.message}
        severity={confirm.severity} onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm(p => ({ ...p, open: false }))} />
    </div>
  );
};

export default ICOverrides;
